// The simplified phone editor (SPEC §5): pick a sound. Drums: pick a bar, toggle 16 steps.
// Instruments (bass, harmony, lead, classic) and their extra lanes: the note lane, a scale-locked
// piano roll (NoteLane.tsx); "+ <sound>" adds an extra lane with another of the instrument's sounds.
import { useMemo, useState } from "react";
import {
  defaultMix,
  INSTRUMENT_ROLES,
  LOOP_BARS,
  loopSound,
  NUM_SOUNDS,
  ROLE_LABEL,
  scaleNotesInRange,
  slotRange,
  TICKS_PER_BAR,
  isAtmosPad,
  isInstrumentSlot,
} from "@eb/shared";
import { NoteLane } from "./NoteLane";
import { addableLanes, addLane, addNote, noteInCell, removeNote } from "./ops";
import { previewSound } from "./padInput";
import { edit, editSong, useEditor, useSongKey, useView } from "./store";

const STEP = TICKS_PER_BAR / 16;

export function PhoneEditor() {
  const spec = useEditor((s) => s.spec);
  const def = useEditor((s) => s.def);
  const loop = useView();
  const readOnly = useEditor((s) => s.readOnly);
  const [slot, setSlot] = useState(0);
  const [bar, setBar] = useState(0);
  const songKey = useSongKey();
  const notes = useMemo(() => {
    const r = slotRange(slot);
    return r && songKey ? scaleNotesInRange(songKey, r.lo, r.hi) : [];
  }, [slot, songKey]);
  if (!spec || !def) return null;
  return (
    <section className="phone-editor panel" aria-label="Step editor">
      <div className="phone-sounds" role="radiogroup" aria-label="Sound">
        {[...spec.sounds.keys(), ...(loop.lanes ?? [])].map((i) => (
          <button
            key={i}
            type="button"
            role="radio"
            aria-checked={slot === i}
            className={`chip-btn${slot === i ? " on" : ""}${isInstrumentSlot(i) ? " inst" : isAtmosPad(i) ? " atmos" : ""}`}
            onClick={() => {
              setSlot(i);
              previewSound(i, isInstrumentSlot(i) ? (notes[0] ?? -1) : -1, 100);
            }}
          >
            {i < 16
              ? `${i + 1} ${spec.sounds[i].name}`
              : isAtmosPad(i)
                ? `✦ ${spec.sounds[i].name}`
                : i < NUM_SOUNDS
                  ? ROLE_LABEL[INSTRUMENT_ROLES[i - 16]]
                  : `+ ${loopSound(spec, undefined, i)?.name ?? ""}`}
          </button>
        ))}
      </div>
      {isInstrumentSlot(slot) && slot < NUM_SOUNDS && !readOnly && addableLanes(loop, slot).length > 0 && (
        <div className="phone-sounds" aria-label="Add a lane">
          {addableLanes(loop, slot).map((p) => (
            <button
              key={p}
              type="button"
              className="chip-btn"
              title="Add a lane with this sound next to the main one"
              onClick={() => {
                editSong((l) => addLane(l, p, defaultMix(spec)));
                setSlot(p);
              }}
            >
              + Lane: {loopSound(spec, undefined, p)?.name}
            </button>
          ))}
        </div>
      )}
      {isInstrumentSlot(slot) ? (
        <NoteLane slot={slot} />
      ) : (
        <>
          <div className="phone-bars" role="radiogroup" aria-label="Bar">
            {Array.from({ length: LOOP_BARS }, (_, b) => (
              <button
                key={b}
                type="button"
                role="radio"
                aria-checked={bar === b}
                className={`chip-btn${bar === b ? " on" : ""}`}
                onClick={() => setBar(b)}
              >
                {b + 1}
              </button>
            ))}
          </div>
          <div className="phone-steps" role="group" aria-label={`Steps of bar ${bar + 1}`}>
            {Array.from({ length: 16 }, (_, k) => {
              const tick = bar * TICKS_PER_BAR + k * STEP;
              const on = noteInCell(loop.events, slot, 60, tick, STEP);
              return (
                <button
                  key={k}
                  type="button"
                  className={`step${on ? " on" : ""}${k % 4 === 0 ? " beat" : ""}`}
                  aria-pressed={!!on}
                  aria-label={`Step ${k + 1}`}
                  disabled={readOnly}
                  data-testid={`step-${k}`}
                  onClick={() => {
                    if (on) edit((l) => removeNote(l, on));
                    else {
                      edit((l) =>
                        addNote(
                          l,
                          { tick, pad: slot, note: 60, velocity: 100, length_ticks: STEP },
                          songKey ?? def.key,
                        ),
                      );
                      previewSound(slot, -1, 100);
                    }
                  }}
                />
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}
